/**
 * Project Billing Calculations Utility
 * 
 * Core business logic for project-wide billing calculations
 * Combines period calculations, data fetching, and cost analysis
 */

import { LLMCall, LLMProject } from '../API';
import { PeriodData, calculatePeriodBounds, getDefaultPeriodType } from '../utils/periodCalculations';
import { BillingMetrics, calculateBillingMetrics, ForecastOptions, calculateCallCost, getModelTier } from '../utils/forecastingUtils';
import { 
  fetchLLMCallsByProjectIdAndPeriod, 
  fetchLLMProjectCallsPreview, 
  ProjectCallsResult, 
  ProjectBillingPerformanceMetrics, 
  logProjectBillingPerformance 
} from '../dataaccess/fetchLLMCallsByProjectIdAndPeriod';

export interface ProjectBillingData {
  projectId: string;
  project: LLMProject | null;
  period: PeriodData;
  metrics: BillingMetrics;
  callsData: ProjectCallsResult;
  performance: ProjectBillingPerformanceMetrics;
  lastUpdated: Date;
}

export interface ProjectCostSummary {
  projectId: string;
  projectName: string;
  dailyCosts: DailyCostData[];
  totalCost: number;
  totalCalls: number;
  period: PeriodData;
  lastUpdated: Date;
}

export interface DailyCostData {
  date: string; // YYYY-MM-DD format
  calls: number;
  totalCost: number;
  standardCost: number;
  premiumCost: number;
  superPremiumCost: number;
  reasoningCost: number;
  standardCalls: number;
  premiumCalls: number;
  superPremiumCalls: number;
  reasoningCalls: number;
}

export interface MultiProjectCostData {
  projects: ProjectCostSummary[];
  period: PeriodData;
  totalCostAllProjects: number;
  totalCallsAllProjects: number;
  lastUpdated: Date;
}

/**
 * Calculate complete billing data for a project and period
 */
export async function calculateProjectBilling(
  projectId: string,
  project: LLMProject | null,
  periodType: string = getDefaultPeriodType(),
  options: {
    forecastOptions?: ForecastOptions;
    usePreview?: boolean; // For faster loading, limit to 1000 calls
  } = {}
): Promise<ProjectBillingData> {
  
  const {
    forecastOptions = {},
    usePreview = false
  } = options;
  
  // Calculate current period
  const currentPeriod = calculatePeriodBounds(periodType);
  const projectBilling = await calculateSingleProjectPeriodBilling(
    projectId,
    project,
    currentPeriod,
    forecastOptions,
    usePreview
  );
  
  return projectBilling;
}

/**
 * Calculate billing data for a single project period
 */
async function calculateSingleProjectPeriodBilling(
  projectId: string,
  project: LLMProject | null,
  period: PeriodData,
  forecastOptions: ForecastOptions,
  usePreview: boolean
): Promise<ProjectBillingData> {
  
  const calculationStartTime = Date.now();
  
  // Fetch calls data based on preview mode
  const callsData = usePreview 
    ? await fetchLLMProjectCallsPreview(projectId, period)
    : await fetchLLMCallsByProjectIdAndPeriod(projectId, period);
  
  // Calculate billing metrics from the calls
  const metrics = await calculateBillingMetrics(callsData.calls, period, forecastOptions);
  
  const calculationTimeMs = Date.now() - calculationStartTime;
  
  // Build performance metrics
  const performanceMetrics: ProjectBillingPerformanceMetrics = {
    totalCallsProcessed: callsData.totalCount,
    fetchTimeMs: callsData.fetchTimeMs,
    calculationTimeMs,
    pagesRetrieved: callsData.pagesRetrieved,
    cacheHit: false // Could be enhanced with caching
  };
  
  // Log performance for monitoring
  logProjectBillingPerformance(projectId, period, performanceMetrics);
  
  return {
    projectId,
    project,
    period,
    metrics,
    callsData,
    performance: performanceMetrics,
    lastUpdated: new Date()
  };
}

/**
 * Calculate daily cost breakdown for a project
 */
export function calculateProjectDailyCosts(
  calls: LLMCall[],
  period: PeriodData
): DailyCostData[] {
  // Group calls by date
  const dailyDataMap = new Map<string, DailyCostData>();
  
  // Initialize all dates in the period with zero values
  const currentDate = new Date(period.startDate);
  const endDate = period.actualEndDate || period.endDate;
  
  while (currentDate <= endDate) {
    const dateStr = currentDate.toISOString().split('T')[0];
    dailyDataMap.set(dateStr, {
      date: dateStr,
      calls: 0,
      totalCost: 0,
      standardCost: 0,
      premiumCost: 0,
      superPremiumCost: 0,
      reasoningCost: 0,
      standardCalls: 0,
      premiumCalls: 0,
      superPremiumCalls: 0,
      reasoningCalls: 0
    });
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  // Process each call and add to appropriate date
  calls.forEach(call => {
    const callDate = new Date(call.createdAt);
    const dateStr = callDate.toISOString().split('T')[0];
    
    const dailyData = dailyDataMap.get(dateStr);
    if (!dailyData) return; // Skip calls outside our period
    
    // Calculate cost using existing utility
    const callCost = calculateCallCost(call);
    
    // Determine tier based on service group or model
    const tier = getCallTier(call);
    
    // Update daily totals
    dailyData.calls++;
    dailyData.totalCost += callCost;
    
    // Update tier-specific totals
    switch (tier) {
      case 'standard':
        dailyData.standardCost += callCost;
        dailyData.standardCalls++;
        break;
      case 'premium':
        dailyData.premiumCost += callCost;
        dailyData.premiumCalls++;
        break;
      case 'super-premium':
        dailyData.superPremiumCost += callCost;
        dailyData.superPremiumCalls++;
        break;
      case 'reasoning':
        dailyData.reasoningCost += callCost;
        dailyData.reasoningCalls++;
        break;
      default:
        dailyData.standardCost += callCost;
        dailyData.standardCalls++;
    }
  });
  
  // Convert map to sorted array
  return Array.from(dailyDataMap.values()).sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Determine the tier of a call based on service group or model
 * Extended version of getModelTier to support more tiers
 */
function getCallTier(call: LLMCall): string {
  // Check service group first
  if (call.service?.group?.name) {
    const groupName = call.service.group.name.toLowerCase();
    if (groupName.includes('reasoning')) return 'reasoning';
    if (groupName.includes('super') || groupName.includes('high')) return 'super-premium';
    if (groupName.includes('premium')) return 'premium';
  }
  
  // Fall back to model default service group
  if (call.model?.defaultServiceGroup) {
    const defaultGroup = call.model.defaultServiceGroup.toLowerCase();
    if (defaultGroup.includes('reasoning')) return 'reasoning';
    if (defaultGroup.includes('super') || defaultGroup.includes('high')) return 'super-premium';
    if (defaultGroup.includes('premium')) return 'premium';
  }
  
  // Use existing getModelTier as fallback, which returns 'standard' or 'premium'
  const baseTier = getModelTier(call);
  
  // Additional checks for extended tiers based on service/model names
  const serviceName = call.service?.name?.toLowerCase() || '';
  const modelName = call.model?.name?.toLowerCase() || '';
  
  if (serviceName.includes('reasoning') || modelName.includes('reasoning') || 
      serviceName.includes('o1') || modelName.includes('o1')) return 'reasoning';
  if (serviceName.includes('super') || modelName.includes('super')) return 'super-premium';
  
  return baseTier;
}

/**
 * Calculate project cost summary with daily breakdown
 */
export async function calculateProjectCostSummary(
  projectId: string,
  project: LLMProject | null,
  periodType: string = getDefaultPeriodType(),
  usePreview: boolean = false
): Promise<ProjectCostSummary> {
  
  const period = calculatePeriodBounds(periodType);
  
  // Fetch calls data
  const callsData = usePreview 
    ? await fetchLLMProjectCallsPreview(projectId, period)
    : await fetchLLMCallsByProjectIdAndPeriod(projectId, period);
  
  // Calculate daily costs
  const dailyCosts = calculateProjectDailyCosts(callsData.calls, period);
  
  // Calculate totals
  const totalCost = dailyCosts.reduce((sum, day) => sum + day.totalCost, 0);
  const totalCalls = dailyCosts.reduce((sum, day) => sum + day.calls, 0);
  
  return {
    projectId,
    projectName: project?.name || projectId,
    dailyCosts,
    totalCost,
    totalCalls,
    period,
    lastUpdated: new Date()
  };
}

/**
 * Calculate cost data for multiple projects
 */
export async function calculateMultiProjectCosts(
  projects: { id: string; project: LLMProject }[],
  periodType: string = getDefaultPeriodType(),
  usePreview: boolean = true // Default to preview for performance
): Promise<MultiProjectCostData> {
  
  const period = calculatePeriodBounds(periodType);
  const projectSummaries: ProjectCostSummary[] = [];
  
  // Process projects sequentially to avoid overwhelming the API
  for (const { id, project } of projects) {
    try {
      const summary = await calculateProjectCostSummary(id, project, periodType, usePreview);
      projectSummaries.push(summary);
    } catch (error) {
      console.warn(`Failed to calculate costs for project ${id}:`, error);
      // Continue with other projects
    }
  }
  
  // Calculate aggregate totals
  const totalCostAllProjects = projectSummaries.reduce((sum, proj) => sum + proj.totalCost, 0);
  const totalCallsAllProjects = projectSummaries.reduce((sum, proj) => sum + proj.totalCalls, 0);
  
  return {
    projects: projectSummaries,
    period,
    totalCostAllProjects,
    totalCallsAllProjects,
    lastUpdated: new Date()
  };
}

/**
 * Format cost value for display
 */
export function formatProjectCost(cost: number): string {
  if (cost === 0) return '$0.00';
  if (cost < 0.01) return '<$0.01';
  return `$${cost.toFixed(2)}`;
}

/**
 * Format call count for display
 */
export function formatProjectCallCount(count: number): string {
  if (count === 0) return '0';
  if (count < 1000) return count.toString();
  if (count < 1000000) return `${(count / 1000).toFixed(1)}K`;
  return `${(count / 1000000).toFixed(1)}M`;
}

/**
 * Validate project billing calculation inputs
 */
export function validateProjectBillingCalculationInputs(
  projectId: string,
  periodType: string
): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  if (!projectId || projectId.trim() === '') {
    errors.push('Project ID is required');
  }
  
  if (!periodType || periodType.trim() === '') {
    errors.push('Period type is required');
  }
  
  // Validate period type is supported
  const supportedPeriods = ['current-month', 'previous-month', 'current-week', 'previous-week', 'today', 'yesterday'];
  if (!supportedPeriods.includes(periodType)) {
    errors.push(`Unsupported period type: ${periodType}`);
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}
